import React from "react";
import { Link } from "react-router-dom";
import { Footer, Header } from "../../components";
import { STAGES, labelFor, linkFor, spellOut, yearsForStage } from "../../constants/catalogue";
import { C, FONT, container } from "../../constants/mq";

// Everything we teach, on one page.
//
// Primary first, then high school, then senior. High school used to lead
// because it was the part we planned to open first — that stopped being true
// and the page kept the order anyway.
//
// The list is built from the catalogue rather than fetched. The old version
// called an API on a hardcoded IP that stopped resolving a long time ago, so
// this page had been showing an empty grid to every visitor.

const ORDER = ["primary", "high", "senior"];

const BLURB = {
  primary: "WRMT in Years 1–5 (Writing, Reading skills, Mathematical reasoning skills and Thinking skills) and WEM in Year 6 (Writing, English and Mathematics), plus trial test preparation for OC and selective entry.",
  high: "English, Maths and Science, written to the NSW syllabus year by year.",
  senior: "HSC subjects taught by teachers who mark to the same criteria the exam does.",
};

const s = {
  page: { background: "#fff", minHeight: "100vh" },
  hero: {
    background: `linear-gradient(112deg, ${C.bright} 0%, ${C.bright} 66%, #C2001A 66%, #C2001A 100%)`,
    color: "#fff",
    padding: "58px 0 54px",
    fontFamily: FONT,
  },
  container,
  eyebrow: {
    display: "inline-block", fontSize: 12.5, fontWeight: 600, letterSpacing: "1.2px",
    textTransform: "uppercase", color: "rgba(255,255,255,0.85)", margin: "0 0 14px",
  },
  h1: {
    fontSize: 40, fontWeight: 800, letterSpacing: "-0.5px", margin: 0, lineHeight: 1.18,
    color: "#fff", maxWidth: 680,
  },
  lead: { fontSize: 15.5, lineHeight: 1.65, color: "rgba(255,255,255,0.92)", margin: "14px 0 0", maxWidth: 620 },

  body: { padding: "56px 0 72px", fontFamily: FONT },
  stage: { marginBottom: 46 },
  stageHead: {
    display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
    paddingBottom: 12, borderBottom: `2px solid ${C.ink}`, marginBottom: 20,
  },
  stageTitle: { fontSize: 24, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: "-0.3px" },
  stageBlurb: { fontSize: 14, color: C.muted, margin: 0, lineHeight: 1.6 },

  years: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 },
  yearCard: { padding: "18px 20px 14px" },
  yearName: {
    fontSize: 12, fontWeight: 700, color: C.red, letterSpacing: "1.2px",
    textTransform: "uppercase", margin: "0 0 8px",
  },
  // .mq-row supplies the flex layout, colour and chevron
  courseLink: { padding: "10px 0", borderTop: `1px solid ${C.line}`, lineHeight: 1.4 },

  cta: {
    marginTop: 8, padding: "26px 28px", display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 20, flexWrap: "wrap",
  },
  ctaTitle: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 6px" },
  ctaBody: { fontSize: 14, color: C.body, margin: 0, lineHeight: 1.6, maxWidth: 520 },
  ctaRow: { display: "flex", alignItems: "center", gap: "12px 28px", flexWrap: "wrap" },
};

const OurCourses = () => (
  <div style={s.page}>
    <Header />
    <main>
      <section style={s.hero}>
        <div style={s.container}>
          <span style={s.eyebrow}>Our courses</span>
          <h1 style={s.h1}>Kindergarten to the HSC</h1>
          <p style={s.lead}>
            Every course is written for one year level. Pick your child&rsquo;s
            year to see what they would be doing this term.
          </p>
        </div>
      </section>

      <section style={s.body}>
        <div style={s.container}>
          {ORDER.map((key) => {
            const years = yearsForStage(key);
            if (!years.length) return null;
            return (
              <div key={key} style={s.stage}>
                <div style={s.stageHead}>
                  <h2 style={s.stageTitle}>{STAGES[key]}</h2>
                  <p style={s.stageBlurb}>{BLURB[key]}</p>
                </div>
                <div style={s.years}>
                  {years.map((y) => (
                    <div key={y.year} className="mq-card" style={s.yearCard}>
                      <p style={s.yearName}>Year {y.year}</p>
                      {y.subjects.map((sub) => (
                        <Link
                          key={sub.slug}
                          to={linkFor(y.year, sub.name).href}
                          className="mq-row"
                          style={s.courseLink}
                        >
                          <span title={spellOut(labelFor(y.year, sub.slug)) || undefined}>
                            {labelFor(y.year, sub.slug)}
                          </span>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="mq-sand" style={s.cta}>
            <div>
              <p style={s.ctaTitle}>Not sure which one?</p>
              <p style={s.ctaBody}>
                The free assessment shows where your child is sitting against
                their year level, and we will tell you which course fits.
              </p>
            </div>
            <div style={s.ctaRow}>
              <Link to="/enroll" className="mq-btn">Enrol now</Link>
              <Link to="/pricing" className="mq-link">See pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default OurCourses;
